/**
 * The Memory control's transitions (D340): the easing and the blend that carry nodes from one picture's places to the
 * next's, and the camera to a node. No DOM.
 *
 * From RUBRIC Agentic OS v2 (Jay E, RoboNuggets, MIT; see THIRD-PARTY-NOTICES.md): brain/port.js `setView` / `setLayout`
 * keep where every node is on the screen (`points`), switch the picture and blend the old places into the new ones over
 * 850 to 1000 ms; `flyCam` is an ease-out cubic. Same idea here, with our durations.
 */

/** A picture to the next: nodes glide, 400 to 600 ms. */
export const TRANSITION_MS = 520;
/** The camera to a node. */
export const FLY_MS = 600;

export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - clamp01(t), 3);
export const easeInOutCubic = (t: number): number => {
  const x = clamp01(t);
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
};

function clamp01(t: number): number {
  return Math.max(0, Math.min(1, t));
}

/** How far along a tween that began at `start` is at `now`, 0 to 1; a zero duration (reduced motion) is done at once. */
export function tweenProgress(start: number, now: number, duration: number): number {
  if (duration <= 0) return 1;
  return clamp01((now - start) / duration);
}

/** A stagger: each node starts a little after its neighbour (by `delay`, 0 to 1 of the whole), so a layout change ripples. */
export function staggered(e: number, delay: number): number {
  return clamp01((e - delay * 0.35) / (1 - 0.35));
}

/**
 * Blend places: `from` towards `to` by `e` (0 to 1, already eased), into `out`. A node with no earlier place (`NaN`
 * in `from`) starts at its new place and fades in instead of flying in from nowhere; `fresh` marks those.
 */
export function blendPlaces(fromX: Float32Array, fromY: Float32Array, toX: Float32Array, toY: Float32Array, e: number, outX: Float32Array, outY: Float32Array, delays?: Float32Array): Uint8Array {
  const fresh = new Uint8Array(toX.length);
  for (let i = 0; i < toX.length; i++) {
    const fx = fromX[i];
    const fy = fromY[i];
    if (!Number.isFinite(fx) || !Number.isFinite(fy)) {
      outX[i] = toX[i];
      outY[i] = toY[i];
      fresh[i] = 1;
      continue;
    }
    const t = delays ? staggered(e, delays[i]) : e;
    outX[i] = fx + (toX[i] - fx) * t;
    outY[i] = fy + (toY[i] - fy) * t;
  }
  return fresh;
}

/** The motion slider (0 to 1) as a speed multiplier: 0.25 at the left, 1 in the middle, 2.5 at the right. */
export function motionSpeedOf(slider: number): number {
  const s = clamp01(slider);
  return s < 0.5 ? 0.25 + (0.75 * s) / 0.5 : 1 + (1.5 * (s - 0.5)) / 0.5;
}
